import * as React from 'react';
import { useSelector } from "react-redux";
import { selectCurrentUser } from "context/auth/authSlice";
import { Link, useLocation, useNavigate, generatePath } from "react-router-dom";
import { LanguageSelect } from "hooks/useGeolocation";

import {
  useMaterialUIController,
  setDarkMode,
  setDirection,
  setLanguage
} from "context";
import breakpoints from "assets/theme/base/breakpoints";
import { motion } from "framer-motion";
import { styled } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';

import Toolbar from '@mui/material/Toolbar';
import {Grid, Container,  Link as MUILink} from '@mui/material';

// import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import StorefrontIcon from '@mui/icons-material/Storefront';
import DarkMode from '@mui/icons-material/DarkMode';
import LightMode from '@mui/icons-material/LightMode';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MDBox from "dashboard/components/MDBox";
import Switch from "@mui/material/Switch";
import AccountMenu from "dashboard/components/MDMenu";
import ShoppingCart from "components/Home/products/components/ShoppingCart";
import SearchAutoComplete from 'dashboard/components/Costum/AutoComplete/Search';
import Menu from "components/Home/components/megaMenu";

const drawerWidth = "100vw";
const MotionContainer = motion(Container);
const defaultSlugs = {
  fr : "",
  ar : "الرئيسية",
  en : "home"
}
const showLogo = {
  visible : {
    transition:{ 
      ease:"linear", 
      duration: .1,

    },
    height : 70,
    opacity : 1,
  },
  hidden : {
    transition:{ 
      ease: "easeIn", 
      duration: .3,
    },
    height :0,
    opacity : 0,
  }
}

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: 50,
  border : `1px solid rgb(76,110, 137)`,
  backgroundColor:   "rgba(25, 87, 137 , .7)",
  '&:hover': {
    backgroundColor:  "rgb(25, 87, 137)",
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    width: 'auto',
    flexGrow: 1
  },
  height : "44px"
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color:"white"
}));



const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const pattern = /^(\/(en|fr|ar))?\/?$/;

const Navigation = (props) => {
  const [controller, dispatch] = useMaterialUIController();
  const { darkMode, direction, lang } = controller;
  const connectedUser = useSelector(selectCurrentUser);
  const { parseContent, data : { navBar, logo, menu } } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [showHeader, setShowHeader] = React.useState(true);
  const [mobileView, setMobileView] = React.useState(false);

  const location = useLocation();
  const navigateTo = useNavigate();
  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  document.addEventListener("scroll",()=>{
    const positionY = document.getElementsByTagName("html")[0].scrollTop ;
    if( positionY > 100 && showHeader ) {
      return setShowHeader(false);
    }
    if( positionY < 100 && !showHeader ) {
      setShowHeader(true);
    }
  });

  const handleDarkMode = () => {
    setDarkMode(dispatch, !darkMode);
    localStorage.setItem("themMode" , !darkMode);
  }

  const handleLanguages = lang => {
    setLanguage(dispatch, lang);
    setDirection(dispatch, lang === "ar" ? "rtl" : "ltr");
    navigateTo(generatePath("/:lang/:slug/*", { lang, slug : lang ? defaultSlugs[lang] : "" }));
  }
  
  React.useEffect(() => {
    // A function that sets the display state for the DefaultNavbarMobile.
    function displayMobileNavbar() {
      if (window.innerWidth < breakpoints.values.lg) {
        setMobileView(true);
      } else {
        setMobileView(false);
      }
    }

    /** 
     The event listener that's calling the displayMobileNavbar function when 
     resizing the window.
    */
    window.addEventListener("resize", displayMobileNavbar);

    // Call the displayMobileNavbar function to set the state with the initial value.
    displayMobileNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", displayMobileNavbar);
  }, []);

  const drawer = (
    <MDBox onClick={handleDrawerToggle} >
      <DrawerHeader>
          <MDBox sx={{display:  'flex' }} component="div">
            {pattern.test(location.pathname.replace(location.hash,"")) ? <a  className="navbar-brand page-scroll" href="#page-top">
              <MDBox component="img" src={`${process.env.REACT_APP_PUBLIC_URL}/img/logo/${logo}`} alt="logo" sx={{ maxWidth:"50%", p:2, mx: "auto"}} />
            </a> : <MUILink sx={{ textAlign: 'center', cursor:"pointer" }}  component={Link} to={`/`} >
            <MDBox component="img" src={`${process.env.REACT_APP_PUBLIC_URL}/img/logo/${logo}`} alt="logo" sx={{ maxWidth:"50%", p:2, mx: "auto"}} />
              </MUILink>}
          </MDBox>
          <IconButton >
            {direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
      </DrawerHeader>
      <Divider sx={{width : "100%"}} />
      {navBar && (<List  sx={{'& .MuiTypography-root':{fontSize : "16px !important"}}}>
        {navBar.map( (item, index)  => item.active === 1  && (
          <React.Fragment key={`link-${index}`}>
            <ListItem sx={{px:{xs:2}}}>
            {item.link?.type === 2 ? (<a  data-scroll href={`#${item.link.name}`}  style={{ textAlign: 'center', cursor:"pointer" }}>
                {item.icon && <Icon sx={{mt:.5, mr:1}}>{item.icon.name}</Icon>}{item.title}
              </a>) : <MUILink sx={{ textAlign: 'center', cursor:"pointer" }} component={Link} to={`${item.link?.slug}`} >{item.icon && <Icon sx={{mt:.5, mr:1}}>{item.icon.name}</Icon>} {item.title}</MUILink>}
            </ListItem>
            <Divider variant="inset" component="li" />
          </React.Fragment>
        ))}
      </List>)}
    </MDBox>
  );

  const container = document.body ;

  return (
      <AppBar id="menu" sx={{backgroundColor:({palette:{background}})=>`${background.default} !important`}}   position={!location.pathname.includes("dashboard") ? "fixed" : "static"} component="nav">
       <MotionContainer  initial="hidden" animate={showHeader ? "visible" : "hidden"} variants={showLogo}  sx={{ display: "flex",  overflow:"hidden",  px : {xs : 0, lg : 3} }} maxWidth="sm">
        <Grid sx={{py:{lg:0, xs : 0}, justifyContent: {sm : "center", lg : "space-around"} }}  alignItems="center" display="flex" spacing={0}  container>
          <Grid xs={2} lg={2} sx={{display: {xs : "flex", lg : "block"}}} item>
          {pattern.test(location.pathname.replace(location.hash,"")) ? <a  className="navbar-brand page-scroll" href="#page-top">
          <MDBox component="img" src={`${process.env.REACT_APP_PUBLIC_URL}/img/logo/${!mobileView ? logo : "granitex-logo-icon.png"}`} alt="logo" sx={{ maxWidth :!mobileView ? "95%" : "48px", verticalAlign : "top", px : mobileView ? 1 : 0, pr : !mobileView ? 1.5 : 0  }} />
         </a> : <MUILink sx={{ textAlign: 'center', cursor:"pointer" }}  component={Link} to={`/`} >
            <MDBox component="img" src={`${process.env.REACT_APP_PUBLIC_URL}/img/logo/${!mobileView ? logo : "granitex-logo-icon.png"}`} alt="logo"sx={{ maxWidth :!mobileView ? "95%" : "48px", verticalAlign : "top" , px : mobileView ? 1 : 0, pr : !mobileView ? 1.5 : 0}} />
          </MUILink>}
          {mobileView && <Divider orientation="vertical" sx={{  height:"40px", mx:1 }} />}
          </Grid>
          <Grid sx={{display:{xs:"none", lg:"flex"}, ml:1}} lg={3}  item>
            <MDBox component="img" src={`${process.env.REACT_APP_PUBLIC_URL}/img/50ans.png`} alt="logo" sx={{ maxWidth :"70%", verticalAlign : "top", objectFit:"cover", filter: !darkMode ? "brightness(0.6)" : "none"}} />
          </Grid>
          <Grid   flexGrow={1}  alignItems="center"  item>
            <Grid   alignItems="center" sx={{justifyContent : "flex-end"}} spacing={0}  container>
              <Grid  display="flex" alignItems="center" item>
                 <IconButton
                    color="inherit"
                    component={Link}
                    to={`/fr/products`}
                    edge="start"
                    sx={{padding : "8px 2px 8px 8px"}}
                  >
                    <StorefrontIcon size="medium" sx={{  color  : darkMode ? "white" : "rgba(0, 0, 0, 0.54)", pr:0}} />
                  </IconButton>
                  <Divider orientation="vertical" sx={{  height:"40px", mx:.5 }} />
              </Grid>
              <Grid  display="flex" alignItems="center" item>
                  <Switch sx={{px:1}} checked={darkMode} onChange={handleDarkMode} />
                  {darkMode ? <DarkMode sx={{ color :({palette}) => palette.text.main}} fontSize="small" />
                  : <LightMode sx={{ color :({palette}) => palette.text.main}} fontSize="small" />}
              </Grid>
              <Grid  display="flex" alignItems="center" item>
                <Divider orientation="vertical" sx={{  height:"40px", m:1 }} />
                <LanguageSelect selected={lang} setinitvalue={handleLanguages}/>
              </Grid>
              <Grid  display="flex" alignItems="center" item>
                <Divider orientation="vertical" sx={{  height:"40px", mx:1 }} />
                <MDBox ml={{ xs: "auto", lg: 0 }}> 
                  <ShoppingCart
                    color={"default"}
                    badgeColor="error"
                    size="medium"
                    cartType="cart"
                  />
                </MDBox>
              </Grid>
              <Grid  display="flex" alignItems="center" item>
              <Divider orientation="vertical" sx={{ height:"40px", mx:1 }} />
                <MDBox ml={{ xs: 1 }}>
                  {connectedUser ? 
                  <AccountMenu connectedUser={connectedUser} /> :
                  <IconButton
                    color="inherit"
                    component={Link}
                    to="/authentication/sign-in"
                    edge="start"
                  >
                    <AccountBoxIcon size="medium" sx={{  color  : darkMode ? "white" : "rgba(0, 0, 0, 0.54)"}} />
                  </IconButton>}
                </MDBox>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MotionContainer>
        <Toolbar className="navbar-default" sx={{
            p:".5rem !important", 
            backgroundImage: ({ functions: { linearGradient, rgba }, palette: { gradients } }) =>
            `${linearGradient(
              "#23346a" ,
              "#182039",
              180
            )}`,
           boxShadow : ({palette:{coloredShadows}})=>coloredShadows.error,
           minHeight:"80px !important"
           }}>
            <Container sx={{display:"flex", alignItems:"center", justifyContent:"space-between"}}  maxWidth="sm">
              <Search>
                <SearchIconWrapper>
                  <SearchIcon fontSize="medium" />
                </SearchIconWrapper>
                <SearchAutoComplete dir={direction} lang={lang} parseContent={parseContent} />
              </Search>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ ml: 1, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>

            {navBar && <MDBox sx={{ display: { xs: 'none', sm: 'block' }, marginLeft:{lg:"auto"},p:1 }}>
              {navBar.map( (item, index) => item.active === 1 &&  ( //  
                item.link?.type === 2 ? (
                  pattern.test(location.pathname.replace(location.hash,"")) ? <a  data-scroll href={`/#${item.link.name}`}  key={`navigation-${index}-link`} style={{ textAlign: 'center', cursor:"pointer" }}>
                  {item.icon && <Icon sx={{mt:.5, mr:1}}>{item.icon.name}</Icon>}{item.title}
                </a>
                 : <MUILink data-scroll sx={{ textAlign: 'center', cursor:"pointer" }} key={`sub-navigation-${index}-link`} component={Link} to={`/#${item.link.name}`} >{item.icon && <Icon sx={{mt:.5, mr:1}}>{item.icon.name}</Icon>} {item.title} </MUILink>
                ) : <MUILink sx={{ textAlign: 'center', cursor:"pointer" }} key={`navigation-${index}-link-dom`} component={Link} to={`${item.link?.slug}`} >{item.icon && <Icon sx={{mt:.5, mr:1}}>{item.icon.name}</Icon>} {item.title} </MUILink>
              ))}
            </MDBox>}
          </Container>    
        </Toolbar>
        {menu && <Menu data={menu} darkMode={darkMode} limit={3} />}
        <MDBox component="nav" >
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', lg: 'none' },
            '& .MuiPaper-root' : {
              margin :0,
              borderRadius : 0
            },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, height : "100vh !important"},
            '& .MuiListItem-root a':{color:"inherit"}
          }}
        >
          {drawer}
        </Drawer>
      </MDBox>
      </AppBar>
  );
}

export default Navigation;
